import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { HarborWellFormHeader } from "@/components/HarborWellFormHeader";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicAssessment() {
  const params = useParams();
  const navigate = useNavigate();
  const slug = params.slug || "hcwi";
  const [def, setDef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(-1); // -1 = welcome, 0..N = sections, N+1 = complete
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [startedAt] = useState(() => Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [hp, setHp] = useState(""); // honeypot — must stay empty

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/public/assessments/${slug}`);
        setDef(data);
      } catch (e) {
        toast.error("Couldn't load this assessment. Please try again later.");
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const sections = useMemo(() => def?.sections || [], [def]);
  const totalQuestions = useMemo(
    () => sections.reduce((acc, s) => acc + (s.questions?.length || 0), 0),
    [sections]
  );
  const answeredCount = useMemo(() => {
    let n = 0;
    for (const s of sections) {
      for (const q of s.questions || []) {
        const v = responses[q.key];
        if (v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)) n += 1;
      }
    }
    return n;
  }, [responses, sections]);
  // Progress factors in both section progression (so users see forward
  // motion when entering each new section) and answered-count within
  // them. 70% weight on section progression, 30% on answers.
  const sectionPct = sections.length === 0 ? 0 : Math.round(((step + 1) / sections.length) * 100);
  const answeredPct = totalQuestions === 0 ? 0 : Math.round((answeredCount / totalQuestions) * 100);
  const pct = step < 0 ? 0 : Math.min(100, Math.round(sectionPct * 0.7 + answeredPct * 0.3));

  const setAnswer = (key, val) => {
    setResponses((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const toggleMulti = (key, opt) => {
    setResponses((prev) => {
      const cur = Array.isArray(prev[key]) ? prev[key] : [];
      const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
      return { ...prev, [key]: next };
    });
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateCurrentSection = () => {
    if (step < 0 || step >= sections.length) return true;
    const s = sections[step];
    const newErrors = {};
    for (const q of s.questions || []) {
      if (!q.required) continue;
      const v = responses[q.key];
      const empty = v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
      if (empty) newErrors[q.key] = "Required";
    }
    // Quick ZIP validation when present
    if (responses.zip && !/^\d{5}(-\d{4})?$/.test(String(responses.zip).trim())) {
      newErrors.zip = "Enter a 5-digit ZIP";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const next = () => {
    if (!validateCurrentSection()) {
      toast.error("Please answer required questions.");
      return;
    }
    if (step + 1 < sections.length) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      submit();
    }
  };

  const back = () => {
    if (step > -1) setStep(step - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!validateCurrentSection()) return;
    setSubmitting(true);
    try {
      const completion_seconds = Math.round((Date.now() - startedAt) / 1000);
      const { data } = await axios.post(
        `${API_URL}/api/public/assessments/${slug}/submissions`,
        { responses, completion_seconds, hp_field: hp }
      );
      setSubmittedId(data.id);
      setStep(sections.length); // completion screen
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — please try again.");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }
  if (!def) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-8 max-w-md text-center">
          <h1 className="font-display text-xl font-semibold text-[#0F172A]">Assessment not found</h1>
          <p className="text-sm text-[#334155] mt-2">This survey link is no longer active.</p>
        </div>
      </div>
    );
  }

  const isWelcome = step === -1;
  const isComplete = step === sections.length;
  const currentSection = !isWelcome && !isComplete ? sections[step] : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F8F6] via-white to-[#F9F8F6]" data-testid="public-assessment-page">
      <header className="border-b border-[#E5E5E0] bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <HarborWellFormHeader
            eyebrow={def.short_name || "Assessment"}
            size="md"
            testId="public-assessment-brand-header"
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-24">
        {/* Honeypot — invisible. Bots fill it, humans don't. */}
        <input
          type="text" name="hp_field" value={hp} onChange={(e) => setHp(e.target.value)}
          tabIndex={-1} aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", top: "-9999px" }} autoComplete="off"
        />

        {!isWelcome && !isComplete && (
          <ProgressBar step={step} total={sections.length} pct={pct} />
        )}

        {isWelcome && <WelcomeScreen def={def} onStart={() => setStep(0)} />}

        {currentSection && (
          <section className="space-y-5" data-testid={`assessment-section-${currentSection.key}`}>
            <div>
              <div className="hw-eyebrow mb-2">Section {step + 1} of {sections.length}</div>
              <h1 className="font-display text-3xl font-semibold text-[#0F172A]">{currentSection.title}</h1>
            </div>
            <div className="space-y-5">
              {(currentSection.questions || []).map((q, idx) => (
                <QuestionField
                  key={q.key}
                  q={q}
                  index={idx}
                  value={responses[q.key]}
                  error={errors[q.key]}
                  onChange={(v) => setAnswer(q.key, v)}
                  onToggleMulti={(opt) => toggleMulti(q.key, opt)}
                />
              ))}
            </div>
            <div className="flex items-center justify-between pt-4 gap-3">
              <button
                type="button"
                onClick={back}
                data-testid="assessment-back-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-sm font-medium hover:bg-[#F9F8F6]"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <button
                type="button"
                onClick={next}
                disabled={submitting}
                data-testid="assessment-next-btn"
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-50"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {step + 1 === sections.length ? "Submit" : "Continue"}
                {step + 1 !== sections.length && <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          </section>
        )}

        {isComplete && (
          <CompletionScreen
            def={def}
            submittedId={submittedId}
            onContact={() => navigate(def.completion?.cta_path || "/hcwi/contact")}
          />
        )}
      </main>
    </div>
  );
}

function ProgressBar({ step, total, pct }) {
  return (
    <div className="mb-6" data-testid="assessment-progress">
      <div className="flex items-center justify-between text-xs text-[#1E3A8A] mb-1.5">
        <span className="uppercase tracking-wider font-medium">
          Section {step + 1} of {total}
        </span>
        <span data-testid="assessment-progress-pct">{pct}% complete</span>
      </div>
      <div className="h-2 rounded-full bg-[#E5E5E0] overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#1E3A8A] to-[#0EA5E9] transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function WelcomeScreen({ def: assessmentDef, onStart }) {
  const w = assessmentDef.welcome || {};
  return (
    <section className="space-y-6" data-testid="assessment-welcome">
      {assessmentDef.hero_image_url && (
        <div className="rounded-2xl overflow-hidden border border-[#E5E5E0] shadow-sm">
          <img
            src={assessmentDef.hero_image_url}
            alt="HarborWell community"
            className="w-full h-48 sm:h-64 object-cover"
            data-testid="assessment-hero-image"
          />
        </div>
      )}
      <div className="hw-eyebrow">{assessmentDef.short_name || "Community assessment"}</div>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold text-[#0F172A] leading-tight">
        {w.headline || assessmentDef.name}
      </h1>
      <p className="text-base text-[#334155] leading-relaxed">{w.body}</p>
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-[#1E3A8A] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
        <div className="text-sm text-[#334155]">
          <strong className="text-[#0F172A]">Privacy:</strong> {w.privacy_notice}
        </div>
      </div>
      <div className="flex items-center gap-3 text-sm text-[#1E3A8A]">
        <Sparkles className="w-4 h-4" /> Estimated time: {assessmentDef.estimated_minutes || 4} minutes
      </div>
      <button
        type="button"
        onClick={onStart}
        data-testid="assessment-start-btn"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#1E3A8A] text-white text-base font-medium hover:bg-[#172554]"
      >
        Start the survey <ChevronRight className="w-4 h-4" />
      </button>
    </section>
  );
}

function CompletionScreen({ def, submittedId, onContact }) {
  const c = def.completion || {};
  return (
    <section className="space-y-5 text-center max-w-xl mx-auto" data-testid="assessment-completion">
      <div className="w-16 h-16 mx-auto rounded-full bg-[#DCFCE7] text-[#166534] flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8" strokeWidth={1.75} />
      </div>
      <h1 className="font-display text-3xl font-semibold text-[#0F172A]">
        {c.headline || "Thank you."}
      </h1>
      <p className="text-base text-[#334155]">{c.body}</p>
      {submittedId && submittedId !== "drop" && (
        <div className="text-[10px] tracking-wider uppercase text-[#1E3A8A] font-mono" data-testid="assessment-survey-id">
          Survey ID: {submittedId}
        </div>
      )}
      <button
        type="button"
        onClick={onContact}
        data-testid="assessment-contact-cta"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
      >
        {c.cta_label || "Continue to Optional Contact Form"} <ChevronRight className="w-4 h-4" />
      </button>
    </section>
  );
}

function QuestionField({ q, index, value, error, onChange, onToggleMulti }) {
  const labelId = `q-${q.key}`;
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 sm:p-5 space-y-3" data-testid={`assessment-q-${q.key}`}>
      <div>
        <label id={labelId} className="block text-sm font-medium text-[#0F172A]">
          <span className="text-[#1E3A8A] mr-1.5">{index + 1}.</span>
          {q.label}
          {q.required && <span className="text-[#991B1B] ml-1" aria-hidden="true">*</span>}
        </label>
        {q.help && <p className="text-xs text-[#334155] mt-1">{q.help}</p>}
      </div>

      {(q.type === "single_select" || q.type === "rating") && (
        <div className="grid sm:grid-cols-2 gap-1.5">
          {(q.options || []).map((opt) => {
            const active = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                data-testid={`q-${q.key}-opt-${opt.value}`}
                className={`px-3 py-2 rounded-xl border text-sm text-left transition-colors ${
                  active
                    ? "bg-[#E0E7FF] border-[#1E3A8A] text-[#1E3A8A]"
                    : "bg-white border-[#E5E5E0] text-[#334155] hover:border-[#1E3A8A]/40"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      {q.type === "multi_select" && (
        <div className="grid sm:grid-cols-2 gap-1.5">
          {(q.options || []).map((opt) => {
            const active = Array.isArray(value) && value.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onToggleMulti(opt.value)}
                data-testid={`q-${q.key}-opt-${opt.value}`}
                className={`px-3 py-2 rounded-xl border text-sm text-left flex items-start gap-2 transition-colors ${
                  active
                    ? "bg-[#DCFCE7] border-[#166534] text-[#166534]"
                    : "bg-white border-[#E5E5E0] text-[#334155] hover:border-[#1E3A8A]/40"
                }`}
              >
                <span className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                  active ? "bg-[#166534] border-[#166534] text-white" : "border-[#CBD5E1]"
                }`}>
                  {active && <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />}
                </span>
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {q.type === "dropdown" && (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`q-${q.key}-dropdown`}
          className="w-full px-3 py-2 rounded-xl border border-[#E5E5E0] bg-white text-sm text-[#0F172A] focus:outline-none focus:border-[#1E3A8A]"
        >
          <option value="">Select…</option>
          {(q.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )}

      {q.type === "short_text" && (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={q.placeholder}
          maxLength={q.max_length || 200}
          data-testid={`q-${q.key}-input`}
          className="w-full px-3 py-2 rounded-xl border border-[#E5E5E0] bg-white text-sm text-[#0F172A] focus:outline-none focus:border-[#1E3A8A]"
        />
      )}

      {q.type === "long_text" && (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          maxLength={q.max_length || 1000}
          data-testid={`q-${q.key}-textarea`}
          className="w-full px-3 py-2 rounded-xl border border-[#E5E5E0] bg-white text-sm text-[#0F172A] focus:outline-none focus:border-[#1E3A8A]"
        />
      )}

      {error && (
        <div className="text-xs text-[#991B1B]" data-testid={`q-${q.key}-error`}>{error}</div>
      )}
    </div>
  );
}
